import { IterableMap } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { defaultToggleTextOptions } from "@redotech/redo-model/default-values";
import {
  AttachmentStrategy,
  bigcommerceAttachmentStrategies,
  isAttachmentStrategyCheckoutExtensionEnabled,
  isAttachmentStrategyUsingCartToggles,
} from "@redotech/redo-model/returns/attachment-strategy";
import {
  InfoModalVersion,
  Team,
  ToggleFields,
} from "@redotech/redo-model/team";
import { InsertionPosition } from "@redotech/redo-model/widget";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { Button, ButtonBorder, ButtonTheme } from "@redotech/redo-web/button";
import { Card, CollapseSubsection } from "@redotech/redo-web/card";
import {
  ColorPickerBasic,
  Direction,
} from "@redotech/redo-web/color-picker-basic";

import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { FormImageUpload } from "@redotech/redo-web/image-upload";
import { LabelTheme, LabeledInput } from "@redotech/redo-web/labeled-input";
import { FormRadioGroup, RadioGroupLayout } from "@redotech/redo-web/radio";
import { FormSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { PackageProtectionValue } from "@redotech/redo-web/settings-elements/package-protection-elements";
import { FormSwitch } from "@redotech/redo-web/switch";

import {
  FormTextInput,
  InputLines,
  InputTheme,
} from "@redotech/redo-web/text-input";

import {
  InputProvider,
  ListInput,
  groupInput,
  input,
  listInput,
  nonEmptyValidator,
  nonWhitespaceValidator,
} from "@redotech/ui/form";

import { optionalEqual, urlEqual } from "@redotech/util/equal";
import { isDefined } from "@redotech/util/type";
import classNames from "classnames";
import cloneDeep from "lodash/cloneDeep";
import { memo, useEffect, useMemo, useState } from "react";
import { ClientUploader } from "../../../client/uploader";
import { TeamContext } from "../../team";
import * as teamCss from "../../team.module.css";
import { ReturnsCoverageValue } from "./returns/coverage";
import { ReturnsGeneralValue } from "./returns/general";
import { AttachmentStrategyPicker } from "./v2/component/attachment-strategy-picker";

type TabValue = "pdp" | "return" | "combined" | "protection";

const infoModalVersions: readonly InfoModalVersion[] = [
  InfoModalVersion.V1,
  InfoModalVersion.V2,
];

export const cartCountModificationInput = groupInput({
  id: input<symbol>({ equal: () => true }),
  selector: input<string>({ validator: nonEmptyValidator }),
  search: input<string>({ validator: nonEmptyValidator }),
  replace: input<string>({ validator: nonEmptyValidator }),
});

const cartCountModificationDefault = (
  id: symbol,
): InputProvider.Value<typeof cartCountModificationInput> => ({
  id,
  selector: "",
  search: "\\d+",
  replace: "%count%",
});

const productCustomizations = groupInput({
  productTitle: input<string>(),
  toggleMainText: input<string>(),
  toggleSubTextPositive: input<string>(),
  toggleSubTextNegative: input<string>(),
  sku: input<string>(),
});

export type ProductCustomizations = InputProvider.Form<
  typeof productCustomizations
>;

export const storefrontForm = groupInput({
  attachmentStrategy: input<AttachmentStrategy>(),
  shopifyProductSource: input<"supplier" | "store">(),
  toggleSubtextEnabled: input<boolean>(),
  BothProductOptions: productCustomizations,
  cartToggle: groupInput({
    useShadowRootInCart: input<boolean>(),
    cartShadowRootSelector: input<string>(),
    selector: input<string>({ validator: nonWhitespaceValidator }),
    placement: input<InsertionPosition>(),
    clickSelector: input<string>(),
    toggleOnColor: input<string>(),
    toggleOffColor: input<string>(),
    toggleCircleColor: input<string>(),
    cartReplaceItems: input<boolean>(),
  }),
  checkbox: groupInput({
    default: input<boolean>(),
    selector: input<string>({ validator: nonWhitespaceValidator }),
    productName: input<string>(),
    text: input<string>(),
    html: input<string>(),
    hideCheckboxBranding: input<boolean>(),
    hideCheckboxPrice: input<boolean>(),
    textAfterRedo: input<string>(),
  }),
  checkboxInfoModal: groupInput({
    text1: input<string>(),
    title1: input<string>(),
    text2: input<string>(),
    title2: input<string>(),
    text3: input<string>(),
    title3: input<string>(),
    logo: input<URL | undefined>({ equal: optionalEqual(urlEqual) }),
    cartIconEnabled: input<boolean>(),
    checkoutIconEnabled: input<boolean>(),
    newLogo: input<URL | undefined>({ equal: optionalEqual(urlEqual) }),
    sideImage: input<URL | undefined>({ equal: optionalEqual(urlEqual) }),
    version: input<InfoModalVersion>(),
  }),
  hideRedoProduct: groupInput({
    enabled: input<boolean>(),
    productSelector: input<string>(),
    cartCountModifications: listInput(
      () => cartCountModificationInput,
      cartCountModificationDefault,
      (i) => i.id,
    ),
    cartPriceModifications: listInput(
      () => cartCountModificationInput,
      cartCountModificationDefault,
      (i) => i.id,
    ),
    cartDiscountModifications: listInput(
      () => cartCountModificationInput,
      cartCountModificationDefault,
      (i) => i.id,
    ),
  }),
  splitProducts: input<boolean>(),
  uniqueVariantSKU: input<boolean>(),
  indexBasedUniqueVariantStartingAt: input<number | undefined>(),
  redoVariantBaseName: input<string>(),
  packageProtectionAutoCheck: input<boolean>(),
  PackageProtectionProductOptions: productCustomizations,
  sku2: input<string>(),
  returnProductOptions: productCustomizations,
  customPdpCss: input<string>(),
  customToggleCss: input<string>(),
  usingCartAndCheckoutToggle: input<boolean>(),
  autoNavigateToCheckoutAfterTimeout: input<boolean>(),
  trackRedoProductInventory: input<boolean>(),
});

export type StorefrontForm = InputProvider.Form<typeof storefrontForm>;
export type StorefrontValue = InputProvider.Value<typeof storefrontForm>;

export const storefrontDefault: StorefrontValue = {
  attachmentStrategy: AttachmentStrategy.CHECKBOX,
  shopifyProductSource: "store",
  toggleSubtextEnabled: true,
  sku2: "",
  BothProductOptions: {
    productTitle: "",
    toggleMainText: "",
    toggleSubTextPositive: "",
    toggleSubTextNegative: "",
    sku: "",
  },
  checkbox: {
    default: true,
    selector: "",
    text: "",
    html: "",
    productName: "",
    hideCheckboxBranding: false,
    hideCheckboxPrice: false,
    textAfterRedo: "",
  },
  checkboxInfoModal: {
    text1: "",
    title1: "",
    text2: "",
    title2: "",
    text3: "",
    title3: "",
    logo: undefined,
    cartIconEnabled: false,
    checkoutIconEnabled: false,
    newLogo: undefined,
    sideImage: undefined,
    version: InfoModalVersion.V1,
  },
  cartToggle: {
    useShadowRootInCart: false,
    cartShadowRootSelector: "",
    selector: "",
    placement: InsertionPosition.BEFORE,
    clickSelector: "",
    toggleOnColor: "",
    toggleOffColor: "",
    toggleCircleColor: "",
    cartReplaceItems: false,
  },
  hideRedoProduct: {
    enabled: false,
    productSelector: "",
    cartCountModifications: [],
    cartPriceModifications: [],
    cartDiscountModifications: [],
  },
  packageProtectionAutoCheck: true,
  PackageProtectionProductOptions: {
    productTitle: "",
    toggleMainText: "",
    toggleSubTextPositive: "",
    toggleSubTextNegative: "",
    sku: "",
  },
  returnProductOptions: {
    productTitle: "",
    toggleMainText: "",
    toggleSubTextPositive: "",
    toggleSubTextNegative: "",
    sku: "",
  },
  uniqueVariantSKU: false,
  indexBasedUniqueVariantStartingAt: undefined,
  redoVariantBaseName: "",
  splitProducts: false,
  customPdpCss: "",
  customToggleCss: "",
  usingCartAndCheckoutToggle: false,
  autoNavigateToCheckoutAfterTimeout: true,
  trackRedoProductInventory: false,
};

export function attachmentStrategyDropdownOptions(
  splitProducts: boolean,
  isBigcommerce: boolean,
) {
  if (isBigcommerce) {
    return bigcommerceAttachmentStrategies;
  }

  const optionsWithoutSplittingProducts = [
    AttachmentStrategy.CHECKBOX,
    AttachmentStrategy.SINGLE_TOGGLE,
    AttachmentStrategy.CART_CARD,
    AttachmentStrategy.CHECK_OUT,
    AttachmentStrategy.SHIPPING,
    AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
    AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING,
    AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE,
    AttachmentStrategy.ANY_WIDGET_PRODUCT,
  ];

  if (splitProducts) {
    return [
      ...optionsWithoutSplittingProducts,
      AttachmentStrategy.DOUBLE_TOGGLE,
      AttachmentStrategy.DOUBLE_CHECK_OUT,
    ];
  } else {
    return optionsWithoutSplittingProducts;
  }
}

export function storefrontTeamToFormValue(team: Team) {
  return {
    attachmentStrategy:
      team.settings.shopifyExtension?.attachmentStrategy ??
      AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
    toggleSubtextEnabled:
      team.settings.shopifyExtension?.toggleSubtextEnabled ?? true,
    BothProductOptions: {
      productTitle:
        team.settings.shopifyExtension?.bothProductNameOverride || "",
      toggleMainText:
        team.settings.shopifyExtension?.toggleTextOptions?.bothProductToggle
          .titleText || "",
      toggleSubTextPositive:
        team.settings.shopifyExtension?.toggleTextOptions?.bothProductToggle
          .positiveSubtext || "",
      toggleSubTextNegative:
        team.settings.shopifyExtension?.toggleTextOptions?.bothProductToggle
          .negativeSubtext || "",
      sku: team.settings.combinedSKU || "",
    },
    cartToggle: {
      useShadowRootInCart: team.settings.useShadowRootInCart || false,
      cartShadowRootSelector: team.settings.cartShadowRootSelector || "",
      selector: team.settings.cartToggleSelector || "",
      placement: team.settings.cartTogglePlacement || InsertionPosition.BEFORE,
      clickSelector: team.settings.cartClickListenerSelector || "",
      toggleOnColor: team.settings.toggleOnColor || "",
      toggleOffColor: team.settings.toggleOffColor || "",
      toggleCircleColor: team.settings.toggleCircleColor || "",
      cartReplaceItems: team.settings.cartReplaceItems ?? false,
    },
    checkbox: {
      default: team.settings.redoAutoCheck,
      html: team.settings.html || "",
      selector: team.settings.checkboxSelector || "",
      productName: team.settings.productName || "",
      text: team.settings.checkboxText || "",
      hideCheckboxBranding: team.settings.hideCheckboxBranding || false,
      hideCheckboxPrice: team.settings.hideCheckboxPrice || false,
      textAfterRedo: team.settings.checkboxTextAfterRedo || "",
    },
    checkboxInfoModal: {
      text1: team.settings.modalTextAdjustments?.text1 || "",
      title1: team.settings.modalTextAdjustments?.title1 || "",
      text2: team.settings.modalTextAdjustments?.text2 || "",
      title2: team.settings.modalTextAdjustments?.title2 || "",
      text3: team.settings.modalTextAdjustments?.text3 || "",
      title3: team.settings.modalTextAdjustments?.title3 || "",
      logo: team.settings.modalLogo
        ? new URL(team.settings.modalLogo)
        : undefined,
      cartIconEnabled: team.settings.infoModal?.cartIconEnabled ?? false,
      checkoutIconEnabled:
        team.settings.infoModal?.checkoutIconEnabled ?? false,
      newLogo: team.settings.infoModal?.logo
        ? new URL(team.settings.infoModal.logo)
        : undefined,
      sideImage: team.settings.infoModal?.sideImage
        ? new URL(team.settings.infoModal.sideImage)
        : undefined,
      version: team.settings.infoModal?.version || InfoModalVersion.V1,
    },
    hideRedoProduct: {
      enabled: !!team.settings.shopifyExtension?.hideRedoProduct?.enabled,
      productSelector:
        team.settings.shopifyExtension?.hideRedoProduct?.productSelector || "",
      cartCountModifications:
        team.settings.shopifyExtension?.hideRedoProduct?.cartCountModifications?.map(
          (x) => ({ id: Symbol(), ...x }),
        ) || [],
      cartPriceModifications:
        team.settings.shopifyExtension?.hideRedoProduct?.cartPriceModifications?.map(
          (x) => ({ id: Symbol(), ...x }),
        ) || [],
      cartDiscountModifications:
        team.settings.shopifyExtension?.hideRedoProduct?.cartDiscountModifications?.map(
          (x) => ({ id: Symbol(), ...x }),
        ) || [],
    },
    packageProtectionAutoCheck:
      team.settings.packageProtectionAutoCheck ?? team.settings.redoAutoCheck,
    PackageProtectionProductOptions: {
      productTitle:
        team.settings.shopifyExtension?.packageProtectionNameOverride || "",
      toggleMainText:
        team.settings.shopifyExtension?.toggleTextOptions
          ?.packageProtectionToggle.titleText || "",
      toggleSubTextPositive:
        team.settings.shopifyExtension?.toggleTextOptions
          ?.packageProtectionToggle.positiveSubtext || "",
      toggleSubTextNegative:
        team.settings.shopifyExtension?.toggleTextOptions
          ?.packageProtectionToggle.negativeSubtext || "",
      sku: team.settings.packageProtectionSKU || "",
    },
    returnProductOptions: {
      productTitle: team.settings.shopifyExtension?.returnNameOverride || "",
      toggleMainText:
        team.settings.shopifyExtension?.toggleTextOptions?.returnToggle
          .titleText || "",
      toggleSubTextPositive:
        team.settings.shopifyExtension?.toggleTextOptions?.returnToggle
          .positiveSubtext || "",
      toggleSubTextNegative:
        team.settings.shopifyExtension?.toggleTextOptions?.returnToggle
          .negativeSubtext || "",
      sku: team.settings.returnsSKU || "",
    },
    shopifyProductSource: team.shopifyCoverageProductSource!,
    sku2: team.settings.sku2 || "",
    splitProducts: team.settings.packageProtection?.splitProducts || false,
    customPdpCss: team.settings.shopifyExtension?.customPdpCss || "",
    customToggleCss: team.settings.shopifyExtension?.customToggleCss || "",
    uniqueVariantSKU: team.settings.uniqueVariantSKU ?? false,
    indexBasedUniqueVariantStartingAt:
      team.settings.indexBasedUniqueVariantStartingAt,
    usingCartAndCheckoutToggle:
      team.settings.shopifyExtension?.usingCartAndCheckoutToggle ?? false,
    autoNavigateToCheckoutAfterTimeout:
      team.settings.shopifyExtension?.autoNavigateToCheckoutAfterTimeout ??
      true,
    redoVariantBaseName:
      team.settings.shopifyExtension?.redoVariantBaseName || "",
    trackRedoProductInventory:
      team.settings.shopifyExtension?.trackRedoProductInventory ?? false,
  };
}

export const storefrontFormValueToTeam = ({
  formValue,
  prevTeamValue,
}: {
  formValue: { storefront: StorefrontValue };
  prevTeamValue: Team;
}): Team => {
  const team = cloneDeep(prevTeamValue);

  team.settings.checkboxText = formValue.storefront.checkbox.text || undefined;
  team.settings.html = formValue.storefront.checkbox.html || undefined;
  team.settings.redoAutoCheck = formValue.storefront.checkbox.default;
  team.settings.checkboxSelector =
    formValue.storefront.checkbox.selector || undefined;
  team.settings.checkboxTextAfterRedo =
    formValue.storefront.checkbox.textAfterRedo || undefined;
  team.settings.hideCheckboxBranding =
    formValue.storefront.checkbox.hideCheckboxBranding;
  team.settings.hideCheckboxPrice =
    formValue.storefront.checkbox.hideCheckboxPrice;
  team.settings.useShadowRootInCart =
    formValue.storefront.cartToggle.useShadowRootInCart;
  team.settings.cartShadowRootSelector =
    formValue.storefront.cartToggle.cartShadowRootSelector;
  team.settings.cartToggleEnabled =
    isAttachmentStrategyUsingCartToggles[
      formValue.storefront.attachmentStrategy
    ];
  team.settings.cartToggleSelector =
    formValue.storefront.cartToggle.selector || undefined;
  team.settings.cartTogglePlacement =
    formValue.storefront.cartToggle.placement || undefined;
  team.settings.cartClickListenerSelector =
    formValue.storefront.cartToggle.clickSelector || undefined;
  team.settings.toggleOnColor =
    formValue.storefront.cartToggle.toggleOnColor || "";
  team.settings.toggleOffColor =
    formValue.storefront.cartToggle.toggleOffColor || "";
  team.settings.toggleCircleColor =
    formValue.storefront.cartToggle.toggleCircleColor || "";
  team.settings.cartReplaceItems =
    formValue.storefront.cartToggle.cartReplaceItems;
  team.settings.productName =
    formValue.storefront.checkbox.productName || undefined;

  team.shopifyCoverageProductSource = formValue.storefront.shopifyProductSource;

  team.settings.packageProtection = {
    enabled: team.settings.packageProtection?.enabled ?? false,
    minPrice: team.settings.packageProtection?.minPrice ?? 0,
    maxPrice: team.settings.packageProtection?.maxPrice ?? 0,
    percentage: team.settings.packageProtection?.percentage ?? 0,
    ...team.settings.packageProtection,
    splitProducts: formValue.storefront.splitProducts,
  };

  team.settings.returnsSKU =
    formValue.storefront.returnProductOptions.sku || "";
  team.settings.packageProtectionSKU =
    formValue.storefront.PackageProtectionProductOptions.sku || "";
  team.settings.combinedSKU = formValue.storefront.BothProductOptions.sku || "";
  team.settings.uniqueVariantSKU =
    formValue.storefront.uniqueVariantSKU ?? false;
  team.settings.indexBasedUniqueVariantStartingAt =
    formValue.storefront.indexBasedUniqueVariantStartingAt;

  team.settings.shopifyExtension = {
    ...team.settings.shopifyExtension,
    customizationVisible:
      team.settings.shopifyExtension?.customizationVisible ?? false,
    customizeCartItem:
      team.settings.shopifyExtension?.customizeCartItem ?? false,
    brandPrefix: team.settings.shopifyExtension?.brandPrefix ?? "",
    bothProductNameOverride:
      formValue.storefront.BothProductOptions.productTitle ?? "",
    packageProtectionNameOverride:
      formValue.storefront.PackageProtectionProductOptions.productTitle ?? "",
    returnNameOverride:
      formValue.storefront.returnProductOptions.productTitle ?? "",
    redoVariantBaseName: formValue.storefront.redoVariantBaseName ?? "",
    toggleTextOptions: {
      returnToggle: {
        titleText:
          formValue.storefront.returnProductOptions.toggleMainText ?? "",
        positiveSubtext:
          formValue.storefront.returnProductOptions.toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.returnProductOptions.toggleSubTextNegative ?? "",
      },
      packageProtectionToggle: {
        titleText:
          formValue.storefront.PackageProtectionProductOptions.toggleMainText ??
          "",
        positiveSubtext:
          formValue.storefront.PackageProtectionProductOptions
            .toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.PackageProtectionProductOptions
            .toggleSubTextNegative ?? "",
      },
      bothProductToggle: {
        titleText: formValue.storefront.BothProductOptions.toggleMainText ?? "",
        positiveSubtext:
          formValue.storefront.BothProductOptions.toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.BothProductOptions.toggleSubTextNegative ?? "",
      },
    },
    attachmentStrategy: formValue.storefront.attachmentStrategy,
    toggleSubtextEnabled: formValue.storefront.toggleSubtextEnabled,
    hideRedoProduct: {
      enabled: formValue.storefront.hideRedoProduct.enabled,
      productSelector: formValue.storefront.hideRedoProduct.productSelector,
      checkoutButtonSelector:
        team.settings.shopifyExtension?.hideRedoProduct
          ?.checkoutButtonSelector ?? "",
      cartCountModifications:
        formValue.storefront.hideRedoProduct.cartCountModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
      cartPriceModifications:
        formValue.storefront.hideRedoProduct.cartPriceModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
      cartDiscountModifications:
        formValue.storefront.hideRedoProduct.cartDiscountModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
    },
    checkoutExtensionEnabled:
      isAttachmentStrategyCheckoutExtensionEnabled[
        formValue.storefront.attachmentStrategy
      ],
    customPdpCss: formValue.storefront.customPdpCss,
    customToggleCss: formValue.storefront.customToggleCss,
    usingCartAndCheckoutToggle: formValue.storefront.usingCartAndCheckoutToggle,
    autoNavigateToCheckoutAfterTimeout:
      formValue.storefront.autoNavigateToCheckoutAfterTimeout,
    trackRedoProductInventory: formValue.storefront.trackRedoProductInventory,
  };
  team.settings.packageProtectionAutoCheck =
    formValue.storefront.packageProtectionAutoCheck;
  team.settings.modalTextAdjustments = {
    text1: formValue.storefront.checkboxInfoModal.text1,
    title1: formValue.storefront.checkboxInfoModal.title1,
    text2: formValue.storefront.checkboxInfoModal.text2,
    title2: formValue.storefront.checkboxInfoModal.title2,
    text3: formValue.storefront.checkboxInfoModal.text3,
    title3: formValue.storefront.checkboxInfoModal.title3,
  };
  team.settings.infoModal = {
    ...team.settings.infoModal,
    cartIconEnabled: formValue.storefront.checkboxInfoModal.cartIconEnabled,
    checkoutIconEnabled:
      formValue.storefront.checkboxInfoModal.checkoutIconEnabled,
    version: formValue.storefront.checkboxInfoModal.version,
    logo: formValue.storefront.checkboxInfoModal.newLogo
      ? formValue.storefront.checkboxInfoModal.newLogo.toString()
      : "",
    sideImage: formValue.storefront.checkboxInfoModal.sideImage
      ? formValue.storefront.checkboxInfoModal.sideImage.toString()
      : "",
  };
  team.settings.modalLogo = formValue.storefront.checkboxInfoModal.logo
    ? formValue.storefront.checkboxInfoModal.logo.toString()
    : "";

  return team;
};

export const StorefrontCard = memo(function StorefrontCard({
  input,
  returnsGeneral,
  returnsCoverage,
  packageProtection,
  setToggleOnColor,
  setToggleOffColor,
  setToggleCircleColor,
  hasBigcommerceIntegration,
}: {
  input: StorefrontForm;
  returnsGeneral: ReturnsGeneralValue;
  returnsCoverage: ReturnsCoverageValue;
  packageProtection: PackageProtectionValue;
  setToggleOnColor: (color: string) => void;
  setToggleOffColor: (color: string) => void;
  setToggleCircleColor: (color: string) => void;
  hasBigcommerceIntegration: boolean;
}) {
  const team = useRequiredContext(TeamContext);

  const {
    cartToggle,
    checkbox,
    hideRedoProduct,
    checkboxInfoModal,
    attachmentStrategy,
    returnProductOptions,
    PackageProtectionProductOptions,
    BothProductOptions,
    packageProtectionAutoCheck,
    splitProducts,
    toggleSubtextEnabled,
    customPdpCss,
    customToggleCss,
    shopifyProductSource,
    sku2,
    indexBasedUniqueVariantStartingAt,
    uniqueVariantSKU,
    redoVariantBaseName,
    usingCartAndCheckoutToggle,
    autoNavigateToCheckoutAfterTimeout,
    trackRedoProductInventory,
  } = input.inputs;

  const {
    useShadowRootInCart: useShadowRootInCart,
    cartShadowRootSelector: cartShadowRootSelector,
    selector: cartToggleSelector,
    placement: cartTogglePlacement,
    clickSelector: cartClickListenerSelector,
    toggleOnColor: toggleOnColor,
    toggleOffColor: toggleOffColor,
    toggleCircleColor: toggleCircleColor,
    cartReplaceItems: cartReplaceItems,
  } = cartToggle.inputs;

  const {
    default: checkboxDefault,
    text: checkboxText,
    html: checkboxHtml,
    selector: checkboxSelector,
    textAfterRedo: checkboxTextAfterRedo,
    hideCheckboxPrice: hideCheckboxPrice,
    hideCheckboxBranding: hideCheckboxBranding,
    productName: productName,
  } = checkbox.inputs;

  const {
    text1: checkboxInfoModalText1,
    title1: checkboxInfoModalTitle1,
    text2: checkboxInfoModalText2,
    title2: checkboxInfoModalTitle2,
    text3: checkboxInfoModalText3,
    title3: checkboxInfoModalTitle3,
    logo: checkboxInfoModalLogo,
    cartIconEnabled: cartIconEnabled,
    checkoutIconEnabled: checkoutIconEnabled,
    newLogo: infoModalNewLogo,
    sideImage: infoModalSideImage,
    version: infoModalVersion,
  } = checkboxInfoModal.inputs;

  const {
    enabled: hideProduct,
    productSelector,
    cartCountModifications,
    cartPriceModifications,
    cartDiscountModifications,
  } = hideRedoProduct.inputs;

  const textDefaults = defaultToggleTextOptions({
    exchange: returnsCoverage.coverage.exchange,
    storeCredit: returnsCoverage.coverage.credit,
    refund: returnsCoverage.coverage.refund,
  });

  const skuDefault = sku2.value;

  const [activeTab, setActiveTab] = useState<TabValue>("pdp");

  const tabOptions = useMemo(() => {
    const baseOptions = [
      { value: "pdp" as TabValue, el: <span>Product detail page</span> },
    ];

    const conditions = {
      combined:
        returnsGeneral.enabled &&
        packageProtection.enabled &&
        packageProtection.coverage,
      return:
        splitProducts.value ||
        (returnsGeneral.enabled &&
          (!packageProtection.enabled || !packageProtection.coverage)),
      protection:
        splitProducts.value ||
        (!returnsGeneral.enabled &&
          packageProtection.enabled &&
          packageProtection.coverage),
    };

    const additionalTabs = {
      combined: {
        value: "combined" as TabValue,
        el: <span>Combined product</span>,
      },
      return: { value: "return" as TabValue, el: <span>Return product</span> },
      protection: {
        value: "protection" as TabValue,
        el: <span>Package protection product</span>,
      },
    };

    return [
      ...baseOptions,
      ...Object.entries(conditions)
        .filter(([_, condition]) => condition)
        .map(([key]) => additionalTabs[key as keyof typeof additionalTabs]),
    ];
  }, [
    splitProducts.value,
    returnsGeneral.enabled,
    packageProtection.enabled,
    packageProtection.coverage,
  ]);

  // If the active tab isn't available anymore, switch to a valid one
  useEffect(() => {
    if (
      tabOptions.length > 0 &&
      !tabOptions.some((o) => o.value === activeTab)
    ) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setActiveTab(tabOptions[0].value);
    }
  }, [activeTab, tabOptions]);

  const isToggle =
    attachmentStrategy.value === AttachmentStrategy.SINGLE_TOGGLE ||
    attachmentStrategy.value === AttachmentStrategy.DOUBLE_TOGGLE ||
    attachmentStrategy.value === AttachmentStrategy.CART_CARD ||
    attachmentStrategy.value === AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT ||
    attachmentStrategy.value === AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING ||
    attachmentStrategy.value === AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE;

  const isCheckOut =
    attachmentStrategy.value === AttachmentStrategy.CHECK_OUT ||
    attachmentStrategy.value === AttachmentStrategy.DOUBLE_CHECK_OUT;

  const showSubtextToggle =
    attachmentStrategy.value === AttachmentStrategy.CHECK_OUT ||
    attachmentStrategy.value === AttachmentStrategy.SINGLE_TOGGLE ||
    attachmentStrategy.value === AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT;

  const showCartAndCheckoutToggle =
    attachmentStrategy.value !== AttachmentStrategy.CHECKBOX;

  const showDoubleAutoCheck =
    splitProducts.value &&
    (attachmentStrategy.value === AttachmentStrategy.DOUBLE_TOGGLE ||
      attachmentStrategy.value === AttachmentStrategy.DOUBLE_CHECK_OUT);

  const modalFields = [
    {
      input: checkboxInfoModalText1,
      label: "Text 1",
      desc: "Text body for first section of info modal",
    },
    {
      input: checkboxInfoModalTitle1,
      label: "Title 1",
      desc: "Title for first section of info modal",
    },
    {
      input: checkboxInfoModalText2,
      label: "Text 2",
      desc: "Text body for second section of info modal",
    },
    {
      input: checkboxInfoModalTitle2,
      label: "Title 2",
      desc: "Title for second section of info modal",
    },
    {
      input: checkboxInfoModalText3,
      label: "Text 3",
      desc: "Text body for third section of info modal",
    },
    {
      input: checkboxInfoModalTitle3,
      label: "Title 3",
      desc: "Title for third section of info modal",
    },
  ];

  return (
    <Card title="Storefront">
      <section className={gridCss.grid}>
        {showDoubleAutoCheck && (
          <>
            <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
              <FormSwitch input={checkboxDefault} label="Return Autocheck">
                Return toggle on/off by default
              </FormSwitch>
            </div>
            <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
              <FormSwitch
                input={packageProtectionAutoCheck}
                label="Package protection autocheck"
              >
                Package protection toggle on/off by default
              </FormSwitch>
            </div>
          </>
        )}
        {!showDoubleAutoCheck && (
          <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
            <FormSwitch input={checkboxDefault} label="Autocheck">
              Coverage added by default
            </FormSwitch>
          </div>
        )}
        <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
          <FormTextInput
            description="Name of Shopify product for cart, checkout, order summary, etc. Should always be used if PDP text override is filled so that the titles will match."
            input={productName}
            label="Product name"
            placeholder="Default"
          />
        </div>

        <div className={gridCss.span12}>
          <Divider />
        </div>

        <div className={gridCss.span12}>
          <strong>Coverage product</strong>
        </div>
        <div className={gridCss.span12}>
          {team?._shopify && (
            <FormRadioGroup
              input={shopifyProductSource}
              label="Shopify product source"
              layout={RadioGroupLayout.HORIZONTAL}
              optionLabel={(value) => {
                switch (value) {
                  case "supplier":
                    return (
                      <Flex>
                        Collective
                        <RedoBadge color="purple" size="xs" text="Beta" />
                      </Flex>
                    );
                  case "store":
                    return "Store";
                }
                throw new Error("Unknown coverage product source");
              }}
              options={["supplier", "store"]}
            >
              Coverage product source. Collective will automatically bill
              through Shopify Payments.
            </FormRadioGroup>
          )}
        </div>
        {returnsGeneral.enabled &&
          packageProtection.enabled &&
          packageProtection.coverage && (
            <div className={gridCss.span6Xl}>
              <FormSwitch input={splitProducts} label="Split products">
                Create separate package protection and return coverage products
              </FormSwitch>
            </div>
          )}
        <div className={gridCss.span6Xl}>
          <FormSwitch input={uniqueVariantSKU} label="Unique SKUs">
            Create unique SKUs for every product and Variant
          </FormSwitch>
        </div>
        <div className={gridCss.span6Xl}>
          <FormSwitch
            input={trackRedoProductInventory}
            label="Track product inventory"
          >
            Enable inventory tracking for Redo products in Shopify
          </FormSwitch>
        </div>
        <div
          className={classNames(
            gridCss.span12,
            gridCss.span6Xl,
            teamCss.monospace,
          )}
        >
          <FormTextInput
            description="Custom name for product variants. will display as name, name-1, name-2, etc. If not specified, price will be used."
            input={redoVariantBaseName}
            label="Variant subtitle"
            placeholder="$1.98"
          />
        </div>
        <div className={classNames(gridCss.span12)}>
          <AttachmentStrategyPicker
            hasBigcommerceIntegration={hasBigcommerceIntegration}
            input={attachmentStrategy}
            splitProducts={splitProducts.value}
          />
        </div>

        {showSubtextToggle && (
          <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
            <FormSwitch
              input={toggleSubtextEnabled}
              label="Add subtext and image"
            >
              Adds image to toggle and subtext that changes according to toggle
              state
            </FormSwitch>
          </div>
        )}

        {showCartAndCheckoutToggle && (
          <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
            <FormSwitch
              input={usingCartAndCheckoutToggle}
              label="Toggle in Cart and Checkout"
            >
              Turning this on allows the cart and check out experience to
              co-exist. To work, Placement must be set to a cart experience and
              checkout Ui must be enabled via Shopify
            </FormSwitch>
          </div>
        )}

        <div className={gridCss.span12}>
          <Divider />
        </div>

        {(isToggle || isCheckOut) && (
          <>
            <div className={gridCss.span12}>
              <strong>Cart toggle</strong>
            </div>
            <div
              className={classNames(
                gridCss.span12,
                gridCss.span6Xl,
                teamCss.monospace,
              )}
            >
              <FormTextInput
                description="CSS Query selector for cart toggle placement. One toggle will be placed for each element matching this selector."
                input={cartToggleSelector}
                label="Selector override"
                placeholder="Default"
              />
            </div>
            <div
              className={classNames(
                gridCss.span12,
                gridCss.span6Xl,
                teamCss.monospace,
              )}
            >
              <FormTextInput
                description="CSS Query selector for click listener, which will trigger a render of the toggles. Does not override the default selector, but adds an additional one."
                input={cartClickListenerSelector}
                label="Click listener selector"
                placeholder="Default"
              />
            </div>
            <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
              <FormSelectDropdown
                description={
                  <>
                    <p>Where to place the toggle, relative to the selector:</p>

                    <ul style={{ paddingLeft: "16px" }}>
                      <li>
                        <strong>before</strong>: Before the element, an older
                        sibling.
                      </li>
                      <li>
                        <strong>after</strong>: After the element, a younger
                        sibling.
                      </li>
                      <li>
                        <strong>prepend</strong>: Inside the element, the first
                        child.
                      </li>
                      <li>
                        <strong>append</strong>: Inside the element, the last
                        child.
                      </li>
                    </ul>
                  </>
                }
                input={cartTogglePlacement}
                label="Placement"
                options={Object.values(InsertionPosition)}
                placeholder="Default"
              >
                {(s) => s}
              </FormSelectDropdown>
            </div>
            <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
              <LabeledInput label="Toggle colors">
                <ColorPickerBasic
                  label="On"
                  onChange={(color) => {
                    setToggleOnColor(color);
                  }}
                  openDirection={Direction.BELOW}
                  value={toggleOnColor.value}
                />
                <ColorPickerBasic
                  label="Off"
                  onChange={(color) => {
                    setToggleOffColor(color);
                  }}
                  openDirection={Direction.BELOW}
                  value={toggleOffColor.value}
                />
                <ColorPickerBasic
                  label="Circle"
                  onChange={(color) => {
                    setToggleCircleColor(color);
                  }}
                  openDirection={Direction.BELOW}
                  value={toggleCircleColor.value}
                />
              </LabeledInput>
            </div>
          </>
        )}

        <div className={gridCss.span12}>
          <FormSwitch input={useShadowRootInCart} label="Use shadow root cart">
            Whether the merchant's cart is inside a shadow root
          </FormSwitch>
        </div>
        {useShadowRootInCart.value && (
          <div className={classNames(gridCss.span12, teamCss.monospace)}>
            <FormTextInput
              input={cartShadowRootSelector}
              label="Shadow root host selector"
            />
          </div>
        )}
        <div className={gridCss.span12}>
          <FormSwitch
            input={cartReplaceItems}
            label="Place cart product at bottom"
          >
            Whether Redo product is placed at the top or bottom of cart.{" "}
            <strong>
              Use only when the cart's "remove" button unnecessarily removes
              Redo. Increases latency.
            </strong>
          </FormSwitch>
        </div>
        <div className={gridCss.span12}>
          <FormSwitch input={hideProduct} label="Hide cart product">
            Whether Redo product is hidden in cart.
            {hideProduct.value && (
              <>
                {" "}
                <strong>
                  Be extremely careful with these settings! Invalid selectors
                  can significantly impact the store.
                </strong>
              </>
            )}
          </FormSwitch>
        </div>

        {hideProduct.value && (
          <>
            <div className={classNames(gridCss.span12, teamCss.monospace)}>
              <FormTextInput
                description="You can use %handle%, %product_id%, %variant_id%, and %index% as placeholders."
                input={productSelector}
                label="Selector for Redo product."
              />
            </div>
            <div className={classNames(gridCss.span12)}>
              <CartModificationElement
                cartModifications={cartCountModifications}
                replaceDefault="%count%"
                selectorDescription="Selector containing the cart count"
                title="Cart count modifications"
                variableDescription="String to replace it with. %count% and %line_item_count% are the available template variables."
              />
            </div>

            <div className={classNames(gridCss.span12)}>
              <CartModificationElement
                cartModifications={cartPriceModifications}
                replaceDefault="%cart-subtotal%"
                selectorDescription="Selector containing the cart price"
                title="Cart price modifications"
                variableDescription="Options: %cart-total%, %cart-subtotal%, %cart-subtotal|round%, %cart-subtotal|withRedo%"
              />
            </div>

            <div className={classNames(gridCss.span12)}>
              <CartModificationElement
                cartModifications={cartDiscountModifications}
                replaceDefault="%cart-discount%"
                selectorDescription="Selector containing the cart discount"
                title="Cart discount modifications"
                variableDescription="Options: %cart-discount% and %cart-discount|round%"
              />
            </div>
          </>
        )}

        <div className={gridCss.span12}>
          <Divider />
        </div>

        <div className={gridCss.span12}>
          <strong>Info modal</strong>
        </div>

        <div className={gridCss.span12}>
          <FormSelectDropdown
            description="v1 is the original version that explains the steps of the return process. v2 is the new version that explains what Redo offers. NOTE: changing from v2 to v1 will turn off the checkout icon since the old modal is not offered in the checkout."
            input={infoModalVersion}
            label="Info modal version"
            options={infoModalVersions}
            placeholder="Default"
            valueChange={(option) => {
              if (option === InfoModalVersion.V1) {
                checkoutIconEnabled.setValue(false);
              }
            }}
          >
            {(option) => option}
          </FormSelectDropdown>
        </div>

        {infoModalVersion.value === InfoModalVersion.V1 ? (
          <>
            {modalFields.map(({ input, label, desc }) => (
              <div
                className={classNames(
                  gridCss.span12,
                  gridCss.span6Xl,
                  teamCss.monospace,
                )}
                key={label}
              >
                <FormTextInput
                  description={desc}
                  input={input}
                  label={label}
                  placeholder="Default"
                />
              </div>
            ))}
            <div className={classNames(gridCss.span12, teamCss.monospace)}>
              <ClientUploader>
                <FormImageUpload input={checkboxInfoModalLogo} label="Logo" />
              </ClientUploader>
            </div>
            <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
              <FormSwitch
                input={cartIconEnabled}
                label="Show info icon in cart"
              >
                Info icon links to info modal
              </FormSwitch>
            </div>
            <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
              <FormSwitch
                disabled
                input={checkoutIconEnabled}
                label="Show info icon in checkout"
              >
                Info icon links to info modal (Only available for v2)
              </FormSwitch>
            </div>
          </>
        ) : (
          <>
            <div
              className={classNames(
                gridCss.span12,
                gridCss.span6Xl,
                teamCss.monospace,
              )}
            >
              <ClientUploader>
                <FormImageUpload input={infoModalNewLogo} label="Logo" />
              </ClientUploader>
            </div>
            <div
              className={classNames(
                gridCss.span12,
                gridCss.span6Xl,
                teamCss.monospace,
              )}
            >
              <ClientUploader>
                <FormImageUpload
                  input={infoModalSideImage}
                  label="Left side image"
                />
              </ClientUploader>
            </div>

            <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
              <FormSwitch
                input={cartIconEnabled}
                label="Show info icon in cart"
              >
                Info icon links to info modal
              </FormSwitch>
            </div>
            <div className={classNames(gridCss.span12, gridCss.span6Xl)}>
              <FormSwitch
                input={checkoutIconEnabled}
                label="Show info icon in checkout"
              >
                Info icon links to info modal
              </FormSwitch>
            </div>
          </>
        )}

        <div className={gridCss.span12}>
          <Divider />
        </div>

        <div className={gridCss.span12}>
          <strong>Customize copy by country</strong>
        </div>
        <div className={gridCss.span12}>
          <RedoButtonTabGroup<TabValue>
            onChange={setActiveTab}
            options={tabOptions}
            value={activeTab}
          />
        </div>

        {activeTab === "pdp" && (
          <>
            <div
              className={classNames(
                gridCss.span12,
                gridCss.span6Xl,
                teamCss.monospace,
              )}
            >
              <FormTextInput
                description='Replaces all PDP text except "for $price"'
                input={checkboxText}
                label="Text override"
                placeholder="Default"
              />
            </div>
            <div
              className={classNames(
                gridCss.span12,
                gridCss.span6Xl,
                teamCss.monospace,
              )}
            >
              <FormTextInput
                description='Will replace the "Free Unlimited" for the PDP text, more dynamic than PDP text override so is preferred above text override'
                input={checkboxHtml}
                label="HTML override"
                placeholder="Default"
              />
            </div>
            <div
              className={classNames(
                gridCss.span12,
                gridCss.span6Xl,
                teamCss.monospace,
              )}
            >
              <FormTextInput
                description="Selector for PDP placement"
                input={checkboxSelector}
                label="Selector"
                placeholder="Default"
              />
            </div>
            <div
              className={classNames(
                gridCss.span12,
                gridCss.span6Xl,
                teamCss.monospace,
              )}
            >
              <FormTextInput
                description="Additional text appended after the price in the PDP text."
                input={checkboxTextAfterRedo}
                label="Additional text suffix"
                placeholder="Default"
              />
            </div>
            <div className={gridCss.span6Xl}>
              <FormSwitch input={hideCheckboxPrice} label="Hide PDP Price">
                Hide the price of coverage {"\u0028"}for $1.98{"\u0029"} in the
                PDP text.
              </FormSwitch>
            </div>
            <div className={gridCss.span6Xl}>
              <FormSwitch
                input={hideCheckboxBranding}
                label="Hide PDP Branding"
              >
                Hide the small Redo logo on the product detail page AND cart
                toggle.
              </FormSwitch>
            </div>
          </>
        )}

        {activeTab === "combined" && (
          <SplitProductCustomizationElement
            defaultText={textDefaults.bothProductToggle}
            indexBasedUniqueVariantStartingAt={
              indexBasedUniqueVariantStartingAt
            }
            skuDefault={skuDefault}
            toggleFields={BothProductOptions}
          />
        )}

        {activeTab === "return" && (
          <SplitProductCustomizationElement
            defaultText={textDefaults.returnToggle}
            indexBasedUniqueVariantStartingAt={
              indexBasedUniqueVariantStartingAt
            }
            skuDefault={skuDefault}
            toggleFields={returnProductOptions}
          />
        )}

        {activeTab === "protection" && (
          <SplitProductCustomizationElement
            defaultText={textDefaults.packageProtectionToggle}
            indexBasedUniqueVariantStartingAt={
              indexBasedUniqueVariantStartingAt
            }
            skuDefault={skuDefault}
            toggleFields={PackageProtectionProductOptions}
          />
        )}

        <div className={gridCss.span12}>
          <CollapseSubsection title="Advanced options">
            <FormSwitch
              input={autoNavigateToCheckoutAfterTimeout}
              label="Auto-navigate to checkout after timeout"
            >
              Automatically redirect to checkout if the merchant's checkout
              button is unresponsive
            </FormSwitch>
            <FormTextInput
              input={customPdpCss}
              label="Custom PDP CSS"
              lines={InputLines.MULTI}
              theme={InputTheme.FORM}
            />
            <FormTextInput
              description={`Example:
                  [data-target="target1"] {attribute1: value1 !important;
                  attribute2: value2 !important;}
                  [data-target="target2"] {attribute: value !important;}`}
              input={customToggleCss}
              label="Custom Toggle CSS"
              lines={InputLines.MULTI}
              theme={InputTheme.FORM}
            />
          </CollapseSubsection>
        </div>
      </section>
    </Card>
  );
});

const CartModificationElement = memo(function CartModificationElement({
  title,
  cartModifications,
  variableDescription,
  selectorDescription,
  replaceDefault,
}: {
  title: string;
  cartModifications: ListInput<
    InputProvider.Value<typeof cartCountModificationInput>,
    InputProvider.Form<typeof cartCountModificationInput>
  >;
  variableDescription: string;
  selectorDescription: string;
  replaceDefault: string;
}) {
  return (
    <LabeledInput label={title}>
      <IterableMap items={cartModifications.inputs} keyFn={(_, i) => i}>
        {(input) => {
          const { selector, search, replace } = input.inputs;
          return (
            <div className={gridCss.grid}>
              <div className={classNames(teamCss.monospace, gridCss.span4)}>
                <FormTextInput
                  description={selectorDescription}
                  input={selector}
                  label="Selector"
                  labelTheme={LabelTheme.THIN}
                />
              </div>
              <div className={classNames(teamCss.monospace, gridCss.span4)}>
                <FormTextInput
                  description="Regular expression to match"
                  input={search}
                  label="Search"
                  labelTheme={LabelTheme.THIN}
                />
              </div>
              <div className={classNames(teamCss.monospace, gridCss.span4)}>
                <FormTextInput
                  description={variableDescription}
                  input={replace}
                  label="Replace"
                  labelTheme={LabelTheme.THIN}
                />
              </div>
            </div>
          );
        }}
      </IterableMap>
      <Button
        border={ButtonBorder.LIGHT}
        onClick={() =>
          cartModifications.setValue([
            ...cartModifications.value,
            {
              id: Symbol(),
              selector: "",
              search: "\\d+",
              replace: replaceDefault,
            },
          ])
        }
        theme={ButtonTheme.OUTLINED}
      >
        Add
      </Button>
    </LabeledInput>
  );
});

const SplitProductCustomizationElement = memo(
  function SplitProductCustomizationElement({
    toggleFields,
    defaultText,
    skuDefault,
    indexBasedUniqueVariantStartingAt,
  }: {
    toggleFields: ProductCustomizations;
    defaultText: ToggleFields;
    skuDefault: string;
    indexBasedUniqueVariantStartingAt: InputProvider.Form<
      typeof storefrontForm
    >["inputs"]["indexBasedUniqueVariantStartingAt"];
  }) {
    const {
      productTitle,
      toggleMainText,
      toggleSubTextPositive,
      toggleSubTextNegative,
      sku,
    } = toggleFields.inputs;
    return (
      <>
        <div
          className={classNames(
            gridCss.span12,
            gridCss.span6Xl,
            teamCss.monospace,
          )}
        >
          <FormTextInput input={productTitle} label="Product title" />
        </div>
        <div
          className={classNames(
            gridCss.span12,
            gridCss.span6Xl,
            teamCss.monospace,
          )}
        >
          <FormTextInput
            description={`Default: ${defaultText.titleText}. Use %price% as a placeholder for the product price.`}
            input={toggleMainText}
            label="Toggle main text"
            placeholder={defaultText.titleText}
          />
        </div>
        <div
          className={classNames(
            gridCss.span12,
            gridCss.span6Xl,
            teamCss.monospace,
          )}
        >
          <FormTextInput
            description={`Default: ${defaultText.positiveSubtext}`}
            input={toggleSubTextPositive}
            label="Subtext when enabled"
            placeholder={defaultText.positiveSubtext}
          />
        </div>
        <div
          className={classNames(
            gridCss.span12,
            gridCss.span6Xl,
            teamCss.monospace,
          )}
        >
          <FormTextInput
            description={`Default: ${defaultText.negativeSubtext}`}
            input={toggleSubTextNegative}
            label="Subtext when disabled"
            placeholder={defaultText.negativeSubtext}
          />
        </div>
        <div
          className={classNames(
            gridCss.span12,
            gridCss.span6Xl,
            teamCss.monospace,
          )}
        >
          <FormTextInput
            input={sku}
            label="SKU"
            placeholder={skuDefault || "x-redo"}
          />
        </div>
        <div className={classNames(gridCss.span6Xl, teamCss.monospace)}>
          {!isDefined(indexBasedUniqueVariantStartingAt.value) ? (
            <LabeledInput label="SKU numbering starts at">
              <RedoButton
                hierarchy="primary"
                onClick={() => indexBasedUniqueVariantStartingAt.setValue(0)}
                text="Enable SKU numbering"
              />
            </LabeledInput>
          ) : (
            <LabeledInput label="SKU numbering starts at">
              <Flex align="center" gap="xs">
                <RedoIncrementDecrement
                  min={0}
                  placeholder="0"
                  setValue={indexBasedUniqueVariantStartingAt.setValue}
                  value={indexBasedUniqueVariantStartingAt.value}
                />
                <RedoButton
                  onClick={() =>
                    indexBasedUniqueVariantStartingAt.setValue(undefined)
                  }
                  text="Disable"
                />
              </Flex>
            </LabeledInput>
          )}
        </div>

        <div className={gridCss.span12}>
          <Divider />
        </div>
      </>
    );
  },
);
